import React, { useState, useEffect, useMemo } from 'react';
import { getRegistryProps, getLegacyProps } from '../utils/registryProps.jsx';

/**
 * Enhanced block settings hook that supports multiple data sources
 * Automatically detects and uses the best available method for props
 */
export const useEnhancedBlockSettings = (props) => {
    const [registryData, setRegistryData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Extract only the specific props we need to avoid object reference issues
    const { registryId, elementId, block_id } = props || {};

    useEffect(() => {
        let propsData = null;

        // Method 1: Try to use registry ID from element's data attribute
        const element = document.querySelector('[data-theme-id]');
        const elementRegistryId = element?.getAttribute('data-registry-id');
        
        if (elementRegistryId || registryId || block_id) {
            propsData = getRegistryProps(elementRegistryId || registryId || block_id);
        }
        
        // Method 2: Try legacy props if no registry data found
        if (!propsData && (elementId || element?.id)) {
            propsData = getLegacyProps(elementId || element?.id);
        }
        
        // Method 3: Use passed props directly (fallback)
        if (!propsData && props) {
            propsData = { data: props };
        }

        setRegistryData(propsData);
        setLoading(false);
    }, [registryId, elementId, block_id]); // Only depend on specific values, not the entire props object

    // Extract block settings based on data source
    const extractSettings = (data) => {
        if (!data) {
            return {
                blockId: null,
                blockSettings: {},
                hasSettings: false,
                globals: {},
                product: null,
                article: null,
                collection: null
            };
        }

        // New registry format
        if (data.data) {
            return {
                blockId: data.data.block_id || data.registryId,
                blockSettings: data.data.block_settings || {},
                hasSettings: !!(data.data.block_settings && Object.keys(data.data.block_settings).length > 0),
                globals: data.data.globals || {},
                product: data.data.product || null,
                article: data.data.article || null,
                collection: data.data.collection || null,
                routes: data.data.routes || {},
                page: data.data.page || {},
                template: data.data.template || {},
                blog: data.data.blog || {}
            };
        }

        // Legacy format fallback
        return {
            blockId: data.settings?.block_id || null,
            blockSettings: data.settings?.block_settings || data.settings || {},
            hasSettings: !!(data.settings && Object.keys(data.settings).length > 0),
            globals: data.globals || {},
            product: data.product || null,
            article: data.article || null,
            collection: data.collection || null,
            routes: data.routes || {},
            page: data.page || {},
            template: data.template || {},
            blog: data.blog || {}
        };
    };

    const settings = extractSettings(registryData);

    return {
        ...settings,
        loading,
        rawData: registryData
    };
};

/**
 * Backward compatible hook - enhanced version of the original useBlockSettings
 * Automatically detects and uses the best available data source
 */
export const useBlockSettings = (props) => {
    const enhanced = useEnhancedBlockSettings(props);
    
    // Return in original format for backward compatibility
    return {
        blockId: enhanced.blockId,
        blockSettings: enhanced.blockSettings,
        hasSettings: enhanced.hasSettings
    };
};

/**
 * Hook for extracting common animation settings (unchanged)
 */
export const useBlockAnimations = (blockSettings) => {
    const enableAnimations = blockSettings.enable_animations || false;
    const animationType = blockSettings.animation_type || 'slide-fade-up';
    const animationDuration = blockSettings.animation_duration || 800;
    const animationDelay = blockSettings.animation_delay || 0;
    const animationTrigger = blockSettings.animation_trigger || 'onscroll';
    
    return {
        enableAnimations,
        animationType,
        animationDuration,
        animationDelay,
        animationTrigger,
        animationProps: {
            enableAnimations,
            animationType,
            animationDuration,
            animationDelay,
            animationTrigger
        }
    };
};

/**
 * Hook for extracting global data from the new registry system
 */
export const useGlobalData = (props) => {
    const enhanced = useEnhancedBlockSettings(props);
    
    return {
        globals: enhanced.globals,
        routes: enhanced.routes,
        page: enhanced.page,
        template: enhanced.template,
        blog: enhanced.blog,
        loading: enhanced.loading
    };
};

/**
 * Hook for extracting context-specific data (product, article, collection)
 */
export const useContextData = (props) => {
    const enhanced = useEnhancedBlockSettings(props);
    
    return {
        product: enhanced.product,
        article: enhanced.article,
        collection: enhanced.collection,
        loading: enhanced.loading
    };
};

/**
 * Block wrapper component that provides optimized props and settings
 */
export const BlockWrapper = ({ children, className = '', ...props }) => {
    const settings = useEnhancedBlockSettings(props);
    
    if (settings.loading) {
        return <div className={`block-loading ${className}`}>Loading...</div>;
    }
    
    return (
        <div className={`block-wrapper ${className}`} data-block-id={settings.blockId}>
            {React.cloneElement(children, { ...settings, ...props })}
        </div>
    );
};

export default useBlockSettings;